import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Button, ButtonGroup, Typography } from "@mui/material";
import {
  ASSET_PREVIEW_SCALE_STEP,
  clampAssetPreviewScale,
  fitAssetPreviewScale,
  loadAssetPreviewViewport,
  saveAssetPreviewViewport,
  type AssetPreviewViewport,
} from "./assetPreviewViewport";

function defaultViewport(
  widthPx: number,
  heightPx: number,
): AssetPreviewViewport {
  if (typeof window === "undefined") {
    return { scale: 0.55, x: 0, y: 0 };
  }
  return {
    scale: fitAssetPreviewScale(
      widthPx,
      heightPx,
      window.innerWidth,
      window.innerHeight,
    ),
    x: 0,
    y: 0,
  };
}

const AssetPreviewStage: React.FC<{
  widthPx: number;
  heightPx: number;
  /** Skip zoom/pan (PNG auto-download / capture). */
  disabled?: boolean;
  toolbarExtra?: React.ReactNode;
  children: React.ReactNode;
}> = ({ widthPx, heightPx, disabled = false, toolbarExtra, children }) => {
  const [viewport, setViewport] = useState<AssetPreviewViewport>(
    () => loadAssetPreviewViewport() ?? defaultViewport(widthPx, heightPx),
  );
  const stageRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    originX: number;
    originY: number;
  } | null>(null);

  useEffect(() => {
    if (disabled) return;
    saveAssetPreviewViewport(viewport);
  }, [disabled, viewport]);

  useEffect(() => {
    const node = stageRef.current;
    if (!node || disabled) return;
    const onNativeWheel = (event: WheelEvent) => {
      event.preventDefault();
      const direction = event.deltaY > 0 ? -1 : 1;
      setViewport((prev) => ({
        ...prev,
        scale: clampAssetPreviewScale(
          prev.scale + direction * ASSET_PREVIEW_SCALE_STEP,
        ),
      }));
    };
    node.addEventListener("wheel", onNativeWheel, { passive: false });
    return () => node.removeEventListener("wheel", onNativeWheel);
  }, [disabled]);

  const setScale = useCallback((nextScale: number) => {
    setViewport((prev) => ({
      ...prev,
      scale: clampAssetPreviewScale(nextScale),
    }));
  }, []);

  const zoomBy = useCallback(
    (delta: number) => {
      setScale(viewport.scale + delta);
    },
    [setScale, viewport.scale],
  );

  const fit = useCallback(() => {
    setViewport(defaultViewport(widthPx, heightPx));
  }, [heightPx, widthPx]);

  const onPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (disabled || event.button !== 0) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    dragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      originX: viewport.x,
      originY: viewport.y,
    };
  };

  const onPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    setViewport({
      ...viewport,
      x: drag.originX + (event.clientX - drag.startX),
      y: drag.originY + (event.clientY - drag.startY),
    });
  };

  const endDrag = (event: React.PointerEvent<HTMLDivElement>) => {
    if (dragRef.current?.pointerId === event.pointerId) {
      dragRef.current = null;
    }
  };

  const captureScale = disabled ? 1 : viewport.scale;
  const captureX = disabled ? 0 : viewport.x;
  const captureY = disabled ? 0 : viewport.y;

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        minHeight: "100vh",
        bgcolor: "#08070B",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          position: "fixed",
          top: 8,
          left: 8,
          zIndex: 11,
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        {disabled ? null : (
          <>
            <ButtonGroup
              size="small"
              variant="outlined"
              sx={{ bgcolor: "background.paper" }}
            >
              <Button onClick={() => zoomBy(-ASSET_PREVIEW_SCALE_STEP)}>-</Button>
              <Button onClick={fit} sx={{ minWidth: 64 }}>
                {Math.round(viewport.scale * 100)}%
              </Button>
              <Button onClick={() => zoomBy(ASSET_PREVIEW_SCALE_STEP)}>+</Button>
            </ButtonGroup>
            <Typography
              sx={{ color: "rgba(255,255,255,0.55)", fontSize: 12, fontWeight: 600 }}
            >
              Arrastra para mover
            </Typography>
          </>
        )}
      </Box>
      {toolbarExtra}
      <Box
        ref={stageRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        sx={{
          minHeight: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "flex-start",
          cursor: disabled ? "default" : dragRef.current ? "grabbing" : "grab",
          userSelect: "none",
          pt: "56px",
        }}
      >
        <Box
          sx={{
            width: widthPx,
            height: heightPx,
            flexShrink: 0,
            transform: `translate(${captureX}px, ${captureY}px) scale(${captureScale})`,
            transformOrigin: "top center",
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
};

export default AssetPreviewStage;
